/**
 * Box Truck Pricing Analytics — does the desk act on what the panel suggests?
 *
 * Every row here is a bid email sent while a suggestion was on screen, with the suggestion recorded beside
 * the number the dispatcher actually used. That comparison is the pilot's whole feedback loop: the
 * backtest can say the model matches history, and only this can say whether a dispatcher agreed with it.
 *
 * Read the ratio, not the count. 1.00 means the desk quoted exactly what was suggested; consistently
 * above means the model is pricing under the market as this desk reads it, consistently below means the
 * opposite, and a wide spread with no bias usually means the suggestion is simply being ignored.
 *
 * Backend: GET /v1/gluon/box-truck-pricing-analytics (any control-panel operator). Read-only.
 */
import * as React from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Chip from '@mui/joy/Chip'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Tooltip from '@mui/joy/Tooltip'
import Typography from '@mui/joy/Typography'
import InsightsIcon from '@mui/icons-material/Insights'
import { ANALYTICS_WINDOW_DAYS, ANALYTICS_WINDOW_LABEL } from '@numeo/types-main-mobile'
import type { BoxTruckPricingAdherence, BoxTruckPricingAnalytics } from '@numeo/types'
import { fetchBoxTruckPricingAnalytics } from './api'

/** The server's own set (`@numeo/types`), which also means this selector finally offers the 180-day
 *  window the router has always accepted. */
const WINDOWS = ANALYTICS_WINDOW_DAYS.map((value) => ({ value, label: ANALYTICS_WINDOW_LABEL[value] }))

const pct = (v: number) => `${Math.round(v * 100)}%`
const money = (v: number) => `$${Math.round(v).toLocaleString('en-US')}`
const signedMoney = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${money(Math.abs(v))}`

/** 1.00 is the model's own answer; the further either way, the less the suggestion is deciding. */
const ratioTone = (ratio: number): 'success' | 'warning' | 'danger' => {
    const drift = Math.abs(ratio - 1)
    return drift <= 0.05 ? 'success' : drift <= 0.15 ? 'warning' : 'danger'
}

const Stat = ({ label, value, hint }: { label: string; value: string; hint?: string }) => (
    <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md', minWidth: 150, flex: 1 }}>
        <Typography level="body-xs" textColor="text.tertiary">
            {label}
        </Typography>
        <Typography level="h3" sx={{ mt: 0.5 }}>
            {value}
        </Typography>
        {hint && (
            <Typography level="body-xs" textColor="text.tertiary">
                {hint}
            </Typography>
        )}
    </Sheet>
)

export default function BoxTruckPricingAnalyticsPage() {
    const [days, setDays] = React.useState(30)
    const [data, setData] = React.useState<BoxTruckPricingAnalytics | null>(null)
    const [loading, setLoading] = React.useState(true)
    const [error, setError] = React.useState('')

    React.useEffect(() => {
        let cancelled = false
        setLoading(true)
        setError('')
        // Cleared with the window. Keeping the old rows while a new window fails left the selector saying
        // "All time" above the 30-day numbers, with the error alert on top — read as the all-time verdict.
        setData(null)
        fetchBoxTruckPricingAnalytics(days)
            .then((analytics) => {
                if (!cancelled) setData(analytics)
            })
            .catch((err: unknown) => {
                if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [days])

    const totals = data?.totals
    return (
        <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                <InsightsIcon />
                <Typography level="h2">Box Truck Pricing Analytics</Typography>
            </Box>
            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                Bid emails sent with a suggestion on screen, and how far the quote that went out sat from it. A ratio of 1.00 is the suggestion followed
                exactly.
            </Typography>

            <Select value={days} onChange={(_, v) => setDays(v ?? 30)} size="sm" sx={{ width: 180, mb: 2 }}>
                {WINDOWS.map((w) => (
                    <Option key={w.value} value={w.value}>
                        {w.label}
                    </Option>
                ))}
            </Select>

            {error && (
                <Alert color="danger" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && data && data.recent.length === 0 && (
                <Alert color="neutral">
                    No bids recorded in this window. A row is written only when a dispatcher sends a bid email while the pricing panel is showing a suggestion —
                    so an empty page means the panel is not in front of anyone yet, not that it is being ignored.
                </Alert>
            )}

            {!loading && data?.scannedCap != null && (
                <Alert color="warning" sx={{ mb: 2 }}>
                    This window holds more than {data.scannedCap.toLocaleString('en-US')} bids, so the figures below describe the most recent{' '}
                    {data.scannedCap.toLocaleString('en-US')} and not the whole window. Narrow the window to read a complete one.
                </Alert>
            )}

            {!loading && data && totals && data.recent.length > 0 && (
                <>
                    {totals.decisions === 0 && (
                        <Alert color="neutral" sx={{ mb: 2 }}>
                            Bids were sent in this window, but none of them can be scored — either the panel offered no pair, or the email body was hand-edited
                            and the rate that went out is not provably the calculator's. Which it was is on each row below.
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
                        <Stat label="Bids with a suggestion" value={String(totals.decisions)} />
                        <Stat label="Median quoted ÷ suggested" value={totals.medianRatio.toFixed(2)} hint="1.00 = followed exactly" />
                        <Stat label="Within 5%" value={pct(totals.withinFivePct)} hint="followed the suggestion" />
                        <Stat label="Quoted above" value={pct(totals.above)} hint="asked the broker for more" />
                        <Stat label="Quoted below" value={pct(totals.below)} hint="asked for less" />
                        <Stat label="Median gap" value={signedMoney(totals.medianGapUsd)} hint="quote minus suggestion" />
                    </Box>

                    <Typography level="title-md" sx={{ mb: 1 }}>
                        By dispatcher
                    </Typography>
                    <Sheet variant="outlined" sx={{ borderRadius: 'md', mb: 3 }}>
                        <Table size="sm" stickyHeader>
                            <thead>
                                <tr>
                                    <th>Dispatcher</th>
                                    <th>Company</th>
                                    <th style={{ width: 90 }}>Bids</th>
                                    <th style={{ width: 120 }}>Median ratio</th>
                                    <th style={{ width: 110 }}>Within 5%</th>
                                    <th style={{ width: 100 }}>Above</th>
                                    <th style={{ width: 100 }}>Below</th>
                                    <th style={{ width: 120 }}>Median gap</th>
                                    <th style={{ width: 150 }}>Last bid</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.dispatchers.map((d: BoxTruckPricingAdherence) => (
                                    <tr key={`${d.projectName}:${d.dispatcherId}`}>
                                        <td>{d.dispatcherEmail || d.dispatcherId}</td>
                                        <td>
                                            <Typography level="body-xs" textColor="text.tertiary">
                                                {d.projectName}
                                            </Typography>
                                        </td>
                                        <td>{d.decisions}</td>
                                        <td>
                                            <Chip size="sm" color={ratioTone(d.medianRatio)} variant="soft">
                                                {d.medianRatio.toFixed(2)}
                                            </Chip>
                                        </td>
                                        <td>{pct(d.withinFivePct)}</td>
                                        <td>{pct(d.above)}</td>
                                        <td>{pct(d.below)}</td>
                                        <td>{signedMoney(d.medianGapUsd)}</td>
                                        <td>
                                            <Typography level="body-xs">{new Date(d.lastDecisionAt).toLocaleString()}</Typography>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>

                    <Divider sx={{ mb: 2 }} />
                    <Typography level="title-md" sx={{ mb: 1 }}>
                        Recent bids
                    </Typography>
                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                        The individual cases behind the aggregate — worth reading when a dispatcher's ratio drifts, because the reason is usually in the lane
                        rather than in the number.
                    </Typography>
                    <Sheet variant="outlined" sx={{ borderRadius: 'md' }}>
                        <Table size="sm" stickyHeader>
                            <thead>
                                <tr>
                                    <th>When</th>
                                    <th>Lane</th>
                                    <th style={{ width: 80 }}>Miles</th>
                                    <th>Broker</th>
                                    <th style={{ width: 110 }}>Suggested</th>
                                    <th style={{ width: 110 }}>Quoted</th>
                                    <th style={{ width: 90 }}>Ratio</th>
                                    <th style={{ width: 110 }}>Evidence</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.recent.map((r, i) => {
                                    const ratio = r.suggestedQuote > 0 ? r.bidRate / r.suggestedQuote : 0
                                    return (
                                        <tr key={`${r.externalId}-${r.createdAt}-${i}`}>
                                            <td>
                                                <Typography level="body-xs">{new Date(r.createdAt).toLocaleString()}</Typography>
                                            </td>
                                            <td>
                                                {r.originCity}, {r.originState} → {r.destinationCity}, {r.destinationState}
                                                <Typography level="body-xs" textColor="text.tertiary">
                                                    {r.dispatcherEmail}
                                                </Typography>
                                            </td>
                                            <td>{Math.round(r.loadedMiles)}</td>
                                            <td>
                                                <Typography level="body-xs">{r.brokerName ?? '—'}</Typography>
                                            </td>
                                            <td>
                                                <Tooltip
                                                    title={`Lane typical ${money(r.referenceRate)} · range ${money(r.typicalLow)}–${money(r.typicalHigh)}`}
                                                >
                                                    <span>{r.suggestedQuote > 0 ? money(r.suggestedQuote) : '—'}</span>
                                                </Tooltip>
                                            </td>
                                            <td>{money(r.bidRate)}</td>
                                            <td>
                                                {/* No pair was offered, so there is nothing to have followed. A red 0.00 here
                                                    read as a dispatcher ignoring the model — the opposite of what happened. */}
                                                <Chip size="sm" color={r.bodyEdited || r.suggestedQuote === 0 ? 'neutral' : ratioTone(ratio)} variant="soft">
                                                    {r.suggestedQuote === 0 ? 'no pair' : r.bodyEdited ? 'edited' : ratio.toFixed(2)}
                                                </Chip>
                                            </td>
                                            <td>
                                                <Typography level="body-xs" textColor="text.tertiary">
                                                    {r.confidence} · {r.laneLoads}/{r.brokerLoads}
                                                </Typography>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </Sheet>
                </>
            )}
        </Box>
    )
}
